'use client';

import { Trans, t } from '@lingui/macro';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HeartBrokenOutlinedIcon from '@mui/icons-material/HeartBrokenOutlined';
import HelpOutlineTwoToneIcon from '@mui/icons-material/HelpOutlineTwoTone';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Button } from '@prismo-io/design-system';
import { useRouter } from '@prismo-io/router';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useSWRConfig } from 'swr';
import { useDeleteSkill } from '../../../hooks/use-delete-skill';
import { useSkillsSidebar } from '../../../hooks/use-skills-sidebar';
import type { KnowledgesT, SkillsT } from '../../../utils/fetch-skills-and-knowledges';
import { SkeletonItems } from '../skeleton-item';
export enum LevelType {
  Aucun = 0,
  Débutant = 1,
  Intermédiaire = 2,
  Avancé = 3,
}
export enum InterestType {
  Aucun = 0,
  Faible = 1,
  Moyen = 2,
  Fort = 3,
}
export const SidebarSkillList: FC = () => {
  const {
    data,
    isLoading
  } = useSkillsSidebar();
  const router = useRouter();
  const pathname = usePathname();
  const {
    mutate
  } = useSWRConfig();
  let sortList: (SkillsT | KnowledgesT)[] = [];
  if (data) {
    sortList = data.skills.concat(data.knowledges).sort((a, b) => Date.parse(b.created_at.toString()) - Date.parse(a.created_at.toString()));
  }
  const acquiredSkillsTotal = sortList.filter(s => s.level && s.level > 0).length;
  // console.log('sortList', sortList, data?.skills, acquiredSkillsTotal);

  const onClick = (skill: SkillsT | KnowledgesT | null) => {
    if (!skill) {
      router.replace(pathname);
      return;
    }
    const query = `?id=${skill.ref.code}&type=${skill.type}`;
    router.replace(pathname + query);
  };
  const {
    trigger: deleteSkill
  } = useDeleteSkill();
  const onDeleteItem = (skill: SkillsT | KnowledgesT) => {
    // console.log(`delete skill ${skill.id}`);
    deleteSkill({
      id: skill.id,
      type: skill.type
    }, {
      onSuccess: async () => {
        router.replace(pathname);
        mutate('skill-quark-sidebar');
      }
    });
  };
  return <>
      <Stack component="header" direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" padding={2} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
        <Stack direction="row" alignItems="center" gap={1} padding={2} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
          <Typography variant="h1" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Mes compétences</Trans>{' '}
            {!isLoading && `(${acquiredSkillsTotal}/${sortList.length})`}
          </Typography>
          <Tooltip placement="right" title={isLoading ? '' : t`Nombre de compétences acquises sur l'ensemble des compétences évaluées`} data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
            <HelpOutlineTwoToneIcon color="primary" data-sentry-element="HelpOutlineTwoToneIcon" data-sentry-source-file="index.tsx" />
          </Tooltip>
        </Stack>
        <Tooltip placement="right" title={t`Revenir à la suggestion de compétences`} data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
          <Button variant="outlined" onClick={() => onClick(null)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">+ Ajouter</Trans>
          </Button>
        </Tooltip>
      </Stack>
      <List data-sentry-element="List" data-sentry-source-file="index.tsx">
        {isLoading && <SkeletonItems />}
        {sortList.length > 0 && sortList.map(skill => {
        return <ListItem key={skill.ref.code} disablePadding secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => onDeleteItem(skill)}>
                    <DeleteOutline />
                  </IconButton>}>
                <ListItemButton onClick={() => onClick(skill)}>
                  {/* TODO: Voir pourquoi ça met un 0 derriere icone cancel */}
                  <ListItemIcon sx={{
              minWidth: 40,
              color: 'transparent'
            }}>
                    {(!skill.level || skill.level <= 0) && skill.interest && skill.interest > 0 && <Tooltip placement="top" title={t`Compétence souhaitée`}>
                          <FavoriteBorderIcon color="error" />
                        </Tooltip>}
                    {(!skill.level || skill.level <= 0) && (!skill.interest || skill.interest <= 0) && <Tooltip placement="top" title={t`Compétence non souhaitée`}>
                          <HeartBrokenOutlinedIcon color="error" />
                        </Tooltip>}
                    {skill.level && skill.level > 0 && <Tooltip placement="top" title={t`Compétences acquises`}>
                        <CheckCircleOutlineIcon color="success" />
                      </Tooltip>}
                  </ListItemIcon>
                  <ListItemText primary={skill.ref.name} secondary={t`Niveau: ${LevelType[skill.level && skill.level > 0 ? skill.level : 0]} | Interêt: ${InterestType[skill.interest && skill.interest > 0 ? skill.interest : 0]}`} sx={{
              '& .MuiListItemText-primary': {
                fontWeight: 700
              },
              '& .MuiListItemText-secondary': {
                fontWeight: 700,
                fontSize: 12,
                color: 'grayLight'
              }
            }} />
                </ListItemButton>
              </ListItem>;
      })}
      </List>
    </>;
};